import React, { useState, useEffect } from 'react';
import '../assets/Helpers.css';

// Tracker component for project stages
const stages = [
    { id: 'draft', label: 'Draft' },
    { id: 'quoted', label: 'Quoted' },
    { id: 'prepare to build', label: 'Prepare to Build' },
    { id: 'manufacturing', label: 'Manufacturing' },
    { id: 'shipped', label: 'Shipped' }
];

export const Tracker = ({ currentStage, mini }) => {
    // Mini trackers start without labels so table cells don't flash overlapping text
    const [showLabels, setShowLabels] = useState(!mini);
    const [clickedStage, setClickedStage] = useState(null);
    const [hoveredStage, setHoveredStage] = useState(null);
    const containerRef = React.useRef(null);

    const currentIndex = stages.findIndex(s => s.id === currentStage);

    // Keep labels in sync with container width as the viewport changes
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const checkWidth = () => {
            setShowLabels(el.offsetWidth > 500);
        };

        checkWidth();
        window.addEventListener('resize', checkWidth);

        let observer;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(checkWidth);
            observer.observe(el);
        }

        return () => {
            window.removeEventListener('resize', checkWidth);
            if (observer) observer.disconnect();
        };
    }, [mini]);

    const shouldShowLabel = (idx) => {
        if (showLabels) return true;
        if (clickedStage === idx) return true;
        if (hoveredStage === idx) return true;
        return false;
    };

    // Checkmark SVG component
    const Checkmark = () => (
        <svg 
            className="tracker__checkmark" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="3" 
            strokeLinecap="round" 
            strokeLinejoin="round"
        >
            <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
    );

    return (
        <div className={`tracker${mini ? ' tracker--mini' : ''}`}> 
            <div 
                ref={containerRef}
                className={`tracker__container${mini ? ' tracker__container--mini' : ''}`}
            >
                {/* Progress line background */}
                <div className="tracker__line-bg"></div>
                
                {/* Progress line filled portion */}
                <div 
                    className="tracker__line-progress" 
                    style={{ 
                        width: currentIndex >= 0 
                            ? `${(currentIndex / (stages.length - 1)) * 100}%` 
                            : '0%' 
                    }}
                ></div>

                {/* Stage nodes */}
                <div className="tracker__stages">
                    {stages.map((stage, idx) => {
                        const isActive = idx === currentIndex;
                        const isComplete = idx < currentIndex;
                        const labelVisible = shouldShowLabel(idx);

                        return (
                            <div
                                key={stage.id}
                                className={
                                    `tracker__stage` +
                                    (mini ? ' tracker__stage--mini' : '') +
                                    (isActive ? ' tracker__stage--active' : '') +
                                    (isComplete ? ' tracker__stage--complete' : '')
                                }
                                onClick={() => setClickedStage(clickedStage === idx ? null : idx)}
                                onMouseEnter={() => setHoveredStage(idx)}
                                onMouseLeave={() => setHoveredStage(null)}
                            >
                                <div
                                    className={
                                        `tracker__circle` +
                                        (mini ? ' tracker__circle--mini' : '') +
                                        (isActive ? ' tracker__circle--active' : '') +
                                        (isComplete ? ' tracker__circle--complete' : '')
                                    }
                                >
                                    {(isComplete || isActive) ? <Checkmark /> : null}
                                </div>
                                {labelVisible && (
                                    <div
                                        className={
                                            `tracker__label` +
                                            (mini ? ' tracker__label--mini' : '') +
                                            (isActive ? ' tracker__label--active' : '') +
                                            (isComplete ? ' tracker__label--complete' : '') +
                                            (!showLabels ? ' tracker__label--tooltip' : '')
                                        }
                                    >
                                        {stage.label}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
